import chroma from 'chroma-js';
import React from 'react';
import { Button, Card, CardBody, CardHeader } from 'reactstrap';

type PolicyComplianceData = {
  compliance_percentage: number;
  total_count: number;
  passed_count: number;
  failed_count: number;
  criticality_counts: {
    urgent: number;
    critical: number;
    serious: number;
    medium: number;
    minimal: number;
  };
};
type PCComplianceProps = {
  id: number;
  compliance_data?: PolicyComplianceData;
};

const PCCompliance: React.FC<PCComplianceProps> = ({ id, compliance_data }) => {
  const handleDashboardDrilldown = () => {
    if (id) {
      const userRole = atob(localStorage.getItem(btoa('approle')) || '');
      window.location.href = `/${userRole}/dashboard?host_id=${id}#pc`;
    }
  };

  if (!compliance_data) {
    return (
      <Card className="shadow h-100 border-0 rounded-4 overflow-hidden">
        <CardHeader className="border-0 bg-transparent pt-4 px-4">
          <h4 className="text-primary mb-0 fw-bold">Policy Compliance</h4>
        </CardHeader>
        <CardBody className="p-4">
          <div className="text-center py-4">
            <h5 className="mb-3 fw-normal text-secondary">
              No Policy Compliance Data
            </h5>
            <p
              className="text-muted mb-0"
              style={{ maxWidth: '280px', margin: '0 auto' }}
            >
              Policy compliance information will appear here when available for
              this host.
            </p>
          </div>
        </CardBody>
      </Card>
    );
  }

  const getComplianceColor = () => {
    return chroma
      .scale([
        '#8B0000', // Dark Red
        '#B22222', // Firebrick
        '#FF4500', // OrangeRed
        '#FF6347', // Tomato
        '#FF7F50', // Coral
        '#32CD32', // LimeGreen
        '#228B22', // ForestGreen
        '#006400', // Dark Green
      ])
      .mode('lab')
      .domain([0, 14, 28, 42, 56, 70, 84, 100])(
        compliance_data.compliance_percentage,
      )
      .hex();
  };
  const complianceColor = getComplianceColor();
  const compliancePercentage = compliance_data.compliance_percentage;
  const dashOffset = 283 - (283 * compliancePercentage) / 100;

  return (
    <Card className="shadow h-100 border-0 rounded-4 overflow-hidden">
      <CardHeader className="border-0 bg-transparent pt-4 px-4">
        <h4 className="text-primary mb-0 fw-bold">Policy Compliance</h4>
      </CardHeader>
      <CardBody className="p-4">
        <div className="d-flex flex-column align-items-center mb-3">
          <div
            className="position-relative"
            style={{ width: '150px', height: '150px' }}
          >
            {/* SVG Circle Progress */}
            <svg width="150" height="150" viewBox="0 0 100 100">
              {/* Background circle */}
              <circle
                cx="50"
                cy="50"
                r="45"
                fill="none"
                stroke="#f0f0f0"
                strokeWidth="8"
              />
              {/* Progress circle */}
              <circle
                cx="50"
                cy="50"
                r="45"
                fill="none"
                stroke={complianceColor}
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray="283"
                strokeDashoffset={dashOffset}
                transform="rotate(-90 50 50)"
              />
            </svg>
            {/* Percentage in the middle */}
            <div
              className="position-absolute d-flex flex-column align-items-center justify-content-center"
              style={{ top: 0, left: 0, right: 0, bottom: 0 }}
            >
              <span className="fs-1 fw-bold">{compliancePercentage}%</span>
              <span className="text-muted small">Compliance</span>
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-around mb-4">
          <div className="text-center">
            <div className="fw-bold fs-4">{compliance_data.total_count}</div>
            <div className="text-muted small">Total Controls</div>
          </div>
          <div className="text-center">
            <div className="fw-bold fs-4 text-success">
              {compliance_data.passed_count}
            </div>
            <div className="text-success small">Passed</div>
          </div>
          <div className="text-center">
            <div className="fw-bold fs-4 text-danger">
              {compliance_data.failed_count}
            </div>
            <div className="text-danger small">Failed</div>
          </div>
        </div>

        <div className="mb-4">
          <h6 className="text-primary mb-3 fw-bold">
            Failed Controls by Severity
          </h6>
          <div className="d-flex justify-content-around flex-wrap mb-3 gap-3">
            {/* Urgent */}
            <div className="text-center">
              <div
                className="d-flex justify-content-center align-items-center rounded-circle mb-2"
                style={{
                  width: '60px',
                  height: '60px',
                  backgroundColor: 'rgba(149, 14, 5, 0.1)', // Urgent background
                }}
              >
                <span className="fs-4" style={{ color: '#950e05' }}>
                  {compliance_data.criticality_counts.urgent}
                </span>
              </div>
              <span
                className="d-block small fw-semibold"
                style={{ color: '#950e05' }}
              >
                Urgent
              </span>
            </div>

            {/* Critical */}
            <div className="text-center">
              <div
                className="d-flex justify-content-center align-items-center rounded-circle mb-2"
                style={{
                  width: '60px',
                  height: '60px',
                  backgroundColor: 'rgba(225, 46, 15, 0.1)', // Critical background
                }}
              >
                <span className="fs-4" style={{ color: '#e12e0f' }}>
                  {compliance_data.criticality_counts.critical}
                </span>
              </div>
              <span
                className="d-block small fw-semibold"
                style={{ color: '#e12e0f' }}
              >
                Critical
              </span>
            </div>

            {/* Serious */}
            <div className="text-center">
              <div
                className="d-flex justify-content-center align-items-center rounded-circle mb-2"
                style={{
                  width: '60px',
                  height: '60px',
                  backgroundColor: 'rgba(236, 37, 13, 0.1)', // Serious background
                }}
              >
                <span className="fs-4" style={{ color: '#ec250d' }}>
                  {compliance_data.criticality_counts.serious}
                </span>
              </div>
              <span
                className="d-block small fw-semibold"
                style={{ color: '#ec250d' }}
              >
                Serious
              </span>
            </div>

            {/* Medium */}
            <div className="text-center">
              <div
                className="d-flex justify-content-center align-items-center rounded-circle mb-2"
                style={{
                  width: '60px',
                  height: '60px',
                  backgroundColor: 'rgba(255, 162, 7, 0.1)', // Medium background
                }}
              >
                <span className="fs-4" style={{ color: '#ffa207' }}>
                  {compliance_data.criticality_counts.medium}
                </span>
              </div>
              <span
                className="d-block small fw-semibold"
                style={{ color: '#ffa207' }}
              >
                Medium
              </span>
            </div>

            {/* Minimal */}
            <div className="text-center">
              <div
                className="d-flex justify-content-center align-items-center rounded-circle mb-2"
                style={{
                  width: '60px',
                  height: '60px',
                  backgroundColor: 'rgba(108, 117, 125, 0.1)', // Minimal background (grey)
                }}
              >
                <span className="fs-4" style={{ color: '#6c757d' }}>
                  {compliance_data.criticality_counts.minimal}
                </span>
              </div>
              <span
                className="d-block small fw-semibold"
                style={{ color: '#6c757d' }}
              >
                Minimal
              </span>
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-end align-items-center mt-4">
          <Button color="primary" onClick={handleDashboardDrilldown}>
            <i className="bi bi-shield-fill-check mr-2"></i>
            View Details
          </Button>
        </div>
      </CardBody>
    </Card>
  );
};

export default PCCompliance;
